import { withRetry } from '@/utils/withRetry';
import { encodePathSegments } from '../graph/encode-path-segments';
import {
  graphErrorWithDetail,
  GraphRequestError,
} from '../graph/graph-request-error';
import { GRAPH_RETRY_OPTIONS } from '../graph/graph-retry';
import { sharePointAuthService } from '../sharepoint-auth/sharepoint-auth-service';
import { OneDriveConsentError } from './onedrive-consent-error';
import {
  ensurePathsFor,
  projectFolderPath,
  SHORTCUT_CREATE_PATH,
  splitOneDrivePath,
} from './onedrive-path';
import {
  OneDriveStepError,
  type OneDriveSyncStep,
} from './onedrive-sync-failure';

const GRAPH_BASE_URL = 'https://graph.microsoft.com/v1.0';

const FILES_SCOPES = ['https://graph.microsoft.com/Files.ReadWrite'];

export interface OneDriveLibraryTarget {
  title: string;
  driveId: string;
}

interface SharePointIds {
  siteId: string;
  siteUrl: string;
  webId: string;
  listId: string;
  listItemUniqueId: string;
}

export type OneDriveSyncStatus =
  | { state: 'unknown' }
  | { state: 'not-synced' }
  | { state: 'synced'; folderWebUrl?: string };

export interface OneDriveSyncOutcome {
  added: string[];
  alreadyPresent: string[];
  failed: { title: string; error: unknown }[];
  folderWebUrl?: string;
}

interface GraphSharePointIdsResponse {
  sharepointIds?: Partial<SharePointIds>;
}

interface GraphShortcutChild {
  id?: string;
  name?: string;
  remoteItem?: {
    parentReference?: { driveId?: string };
    sharepointIds?: Partial<SharePointIds>;
  };
}

interface ShortcutItem {
  id: string;
  name?: string;
}

interface GraphFolder {
  id?: string;
  webUrl?: string;
  folder?: { childCount?: number };
}

interface GraphSearchHit extends GraphShortcutChild {
  parentReference?: { id?: string };
}

const meDriveEndpoint = (path: string, suffix: string): string => {
  const prefix = `${GRAPH_BASE_URL}/me/drive`;
  // Graph rejects the root addressed as `root/`; as an item it is `root`.
  if (path === '/')
    return suffix === '' ? `${prefix}/root` : `${prefix}/root/${suffix}`;

  const separator = suffix === '' ? '' : `/${suffix}`;
  return `${prefix}/root:${encodePathSegments(path)}:${separator}`;
};

// Never match on `remoteItem.id`: every library root reports the same id. Only
// `parentReference.driveId` and `sharepointIds.listId` distinguish them.
const pointsAtLibrary = (
  item: GraphShortcutChild,
  driveId: string,
  listId?: string,
): boolean => {
  const remote = item.remoteItem;
  if (!remote) return false;
  return (
    remote.parentReference?.driveId === driveId ||
    (listId !== undefined && remote.sharepointIds?.listId === listId)
  );
};

const atStep = async <T>(
  step: OneDriveSyncStep,
  run: () => Promise<T>,
): Promise<T> => {
  try {
    return await run();
  } catch (error) {
    if (error instanceof OneDriveStepError) throw error;
    throw new OneDriveStepError(
      step,
      error instanceof Error ? error.message : String(error),
      error,
    );
  }
};

class OneDriveSyncService {
  async getStatus(
    projectTitle: string,
    libraries: OneDriveLibraryTarget[],
  ): Promise<OneDriveSyncStatus> {
    const token =
      await sharePointAuthService.acquireTokenSilentOrNull(FILES_SCOPES);
    if (!token) return { state: 'unknown' };

    const path = projectFolderPath(projectTitle);

    try {
      const folder = await this.#getFolder(path, token);
      if (!folder?.id) return { state: 'not-synced' };
      if (folder.folder?.childCount === 0) return { state: 'not-synced' };

      const items = await this.#searchFolder(folder.id, token);
      const missing = libraries.filter(
        (library) =>
          !items.some((item) => pointsAtLibrary(item, library.driveId)),
      );

      if (missing.length === 0) {
        return { state: 'synced', folderWebUrl: folder.webUrl };
      }

      console.warn('OneDrive probe: some libraries are not synced', {
        folder: path,
        missing: missing.map((library) => library.title),
        found: items.map((item) => item.name),
      });
      return { state: 'not-synced' };
    } catch (error) {
      console.warn('OneDrive probe failed', error);
      return { state: 'unknown' };
    }
  }

  async syncProject(
    projectTitle: string,
    libraries: OneDriveLibraryTarget[],
  ): Promise<OneDriveSyncOutcome> {
    const token = await this.#acquireInteractiveToken();

    const destinationPath = projectFolderPath(projectTitle);
    for (const path of ensurePathsFor(projectTitle)) {
      await atStep('drive-folder', () => this.#ensureFolder(path, token));
    }

    const destination = await atStep('drive-folder', () =>
      this.#getFolder(destinationPath, token),
    );
    const destinationId = destination?.id;
    if (!destinationId) {
      throw new OneDriveStepError(
        'drive-folder',
        `The OneDrive folder ${destinationPath} was not there after being ensured`,
        undefined,
        'destination-missing',
      );
    }

    const present = await this.#searchFolder(destinationId, token).catch(
      (error: unknown) => {
        console.warn('OneDrive presence search failed; continuing', error);
        return [] as GraphSearchHit[];
      },
    );

    const results = await Promise.allSettled(
      libraries.map(async (library) => {
        const ids = await atStep('library-ids', () =>
          this.#getLibraryIds(library.driveId, token),
        );
        const name = library.title;

        if (
          present.some((item) =>
            pointsAtLibrary(item, library.driveId, ids.listId),
          )
        ) {
          return { title: library.title, added: false };
        }

        const occupant = await atStep('shortcut-move', () =>
          this.#getItemByPath(`${destinationPath}/${name}`, token),
        );
        if (occupant) {
          if (pointsAtLibrary(occupant, library.driveId, ids.listId)) {
            return { title: library.title, added: false };
          }
          throw new OneDriveStepError(
            'shortcut-move',
            `${destinationPath}/${name} is taken by an item that is not this library`,
            undefined,
            'destination-name-taken',
          );
        }

        const shortcut = await this.#createOrAdoptShortcut(
          name,
          library.driveId,
          ids,
          token,
        );

        await atStep('shortcut-move', () =>
          this.#moveShortcut(shortcut.id, destinationId, name, token),
        );

        return { title: library.title, added: true };
      }),
    );

    const outcome: OneDriveSyncOutcome = {
      added: [],
      alreadyPresent: [],
      failed: [],
      folderWebUrl: destination?.webUrl,
    };

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        outcome.failed.push({
          title: libraries[index].title,
          error: result.reason,
        });
      } else if (result.value.added) {
        outcome.added.push(result.value.title);
      } else {
        outcome.alreadyPresent.push(result.value.title);
      }
    });

    return outcome;
  }

  async #acquireInteractiveToken(): Promise<string> {
    try {
      return await sharePointAuthService.acquireToken(FILES_SCOPES);
    } catch (error) {
      throw new OneDriveConsentError(error);
    }
  }

  async #getFolder(path: string, token: string): Promise<GraphFolder | null> {
    const endpoint = `${meDriveEndpoint(path, '')}?$select=id,name,webUrl,folder`;

    return withRetry(async () => {
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 404) return null;
      if (!response.ok) {
        throw await graphErrorWithDetail(
          'Failed to read the OneDrive folder',
          response,
        );
      }

      return (await response.json()) as GraphFolder;
    }, GRAPH_RETRY_OPTIONS);
  }

  /**
   * Graph does not list `remoteItem` shortcuts — a folder holding two reports `childCount: 2` and an
   * empty children collection — so enumeration goes through `search(q='')`; `q='*'` is rejected with
   * a 400. Search is recursive and includes the folder itself, so hits are filtered back to direct
   * children by `parentReference.id`.
   */
  async #searchFolder(
    folderId: string,
    token: string,
  ): Promise<GraphSearchHit[]> {
    const endpoint =
      `${GRAPH_BASE_URL}/me/drive/items/${folderId}/search(q='')` +
      `?$select=id,name,parentReference,remoteItem`;

    return withRetry(async () => {
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw await graphErrorWithDetail(
          'Failed to search the OneDrive folder',
          response,
        );
      }

      const body = (await response.json()) as { value?: GraphSearchHit[] };
      return (body.value ?? []).filter(
        (hit) => hit.parentReference?.id === folderId,
      );
    }, GRAPH_RETRY_OPTIONS);
  }

  async #getItemByPath(
    path: string,
    token: string,
  ): Promise<GraphShortcutChild | null> {
    const endpoint = `${meDriveEndpoint(path, '')}?$select=id,name,remoteItem`;

    return withRetry(async () => {
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 404) return null;
      if (!response.ok) {
        throw await graphErrorWithDetail(
          'Failed to read the OneDrive item',
          response,
        );
      }

      return (await response.json()) as GraphShortcutChild;
    }, GRAPH_RETRY_OPTIONS);
  }

  async #createOrAdoptShortcut(
    name: string,
    driveId: string,
    ids: SharePointIds,
    token: string,
  ): Promise<ShortcutItem> {
    const created = await atStep('shortcut-create', () =>
      this.#createShortcut(name, ids, token),
    ).catch((error: unknown) => {
      if (
        error instanceof OneDriveStepError &&
        error.cause instanceof GraphRequestError &&
        error.cause.status === 409
      ) {
        return null;
      }
      throw error;
    });
    if (created) return created;

    const leftover = await atStep('shortcut-create', () =>
      this.#getItemByPath(`/${name}`, token),
    );
    if (leftover?.id && pointsAtLibrary(leftover, driveId, ids.listId)) {
      return { id: leftover.id, name: leftover.name };
    }

    if (!leftover) {
      throw new OneDriveStepError(
        'shortcut-create',
        `/${name} was reported as taken by the create, then resolved to nothing`,
        undefined,
        'root-item-vanished',
      );
    }

    throw new OneDriveStepError(
      'shortcut-create',
      `/${name} at the OneDrive root is taken by an item that is not this library`,
      undefined,
      'root-name-taken',
    );
  }

  async #ensureFolder(path: string, token: string): Promise<void> {
    const { parent, name } = splitOneDrivePath(path);
    const endpoint = meDriveEndpoint(parent, 'children');

    await withRetry(async () => {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          folder: {},
          '@microsoft.graph.conflictBehavior': 'fail',
        }),
      });

      if (response.ok || response.status === 409) return;
      throw await graphErrorWithDetail(
        'Failed to create the OneDrive folder',
        response,
      );
    }, GRAPH_RETRY_OPTIONS);
  }

  async #getLibraryIds(driveId: string, token: string): Promise<SharePointIds> {
    const endpoint = `${GRAPH_BASE_URL}/drives/${driveId}/root?$select=sharepointIds`;

    const body: GraphSharePointIdsResponse = await withRetry(async () => {
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw await graphErrorWithDetail(
          'Failed to read SharePoint ids for the library',
          response,
        );
      }

      return (await response.json()) as GraphSharePointIdsResponse;
    }, GRAPH_RETRY_OPTIONS);

    const ids = body.sharepointIds ?? {};
    const missing = (
      ['siteId', 'siteUrl', 'webId', 'listId', 'listItemUniqueId'] as const
    ).filter((key) => !ids[key]);

    if (missing.length) {
      throw new OneDriveStepError(
        'library-ids',
        `Library drive ${driveId} is missing sharepointIds: ${missing.join(', ')}`,
        undefined,
        'incomplete-ids',
      );
    }

    return ids as SharePointIds;
  }

  async #createShortcut(
    name: string,
    ids: SharePointIds,
    token: string,
  ): Promise<ShortcutItem> {
    const endpoint = meDriveEndpoint(SHORTCUT_CREATE_PATH, 'children');

    return withRetry(async () => {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          remoteItem: { sharepointIds: ids },
          '@microsoft.graph.conflictBehavior': 'fail',
        }),
      });

      // A 409 here is a failure, unlike the 409 swallowed by the folder ensures.
      if (!response.ok) {
        throw await graphErrorWithDetail(
          'Failed to create the OneDrive shortcut',
          response,
        );
      }

      const created = (await response.json()) as ShortcutItem | undefined;
      if (!created?.id) {
        throw new OneDriveStepError(
          'shortcut-create',
          'Graph created the OneDrive shortcut without an id',
          undefined,
          'no-shortcut-id',
        );
      }
      return created;
    }, GRAPH_RETRY_OPTIONS);
  }

  async #moveShortcut(
    itemId: string,
    parentId: string,
    name: string,
    token: string,
  ): Promise<string | undefined> {
    const endpoint = `${GRAPH_BASE_URL}/me/drive/items/${itemId}`;

    return withRetry(async () => {
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parentReference: { id: parentId },
          name,
          '@microsoft.graph.conflictBehavior': 'fail',
        }),
      });

      if (!response.ok) {
        throw await graphErrorWithDetail(
          'Failed to move the OneDrive shortcut into place',
          response,
        );
      }

      try {
        const moved = (await response.json()) as ShortcutItem | undefined;
        return moved?.name;
      } catch {
        return undefined;
      }
    }, GRAPH_RETRY_OPTIONS);
  }
}

export const oneDriveSyncService = new OneDriveSyncService();
